import React, { useRef, useState, useEffect } from "react";
import Table from "../../../core/common/dataTable/index";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import TooltipOption from "../../../core/common/tooltipOption";
import { DatePicker, TimePicker, message, Input, Button } from "antd";
import {
  getEmployeeAttendance,
  markEmployeeAttendance,
  resetEmployeeAttendance,
  type GetAttendanceResponse,
  type InOutRecord,
} from "../../../service/staff-attendance";
import dayjs from "dayjs";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { CLASSES_SORT_OPTIONS } from "../../../core/common/selectoption/selectoption";
import { useSelector } from "react-redux";
import { selectUser } from "../../../core/data/redux/slice/user";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos";

const EmployeeAttendance = () => {
  const routes = all_routes;
  const componentRef = useRef<HTMLDivElement>(null);
  const user = useSelector(selectUser);
  const currentBranch = useSelector(selectCurrentBranch);

  const [attendanceData, setAttendanceData] = useState<
    GetAttendanceResponse["data"]
  >([]);
  const [loading, setLoading] = useState(true);
  const [markingLoading, setMarkingLoading] = useState<Record<string, boolean>>(
    {}
  );
  const [filters, setFilters] = useState({
    date: dayjs().format("YYYY-MM-DD"),
  });

  const [appliedFilters, setAppliedFilters] = useState({
    date: dayjs().format("YYYY-MM-DD"),
    search: "",
    page: 1,
    limit: 10,
    sortBy: "createdAt",
    sortOrder: "desc" as "asc" | "desc",
  });

  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [checkInTimes, setCheckInTimes] = useState<Record<string, string>>({});
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState(CLASSES_SORT_OPTIONS[0]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  );
  const [checkOutTimes, setCheckOutTimes] = useState<Record<string, string>>(
    {}
  );

  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);

      const response = await getEmployeeAttendance({
        date: appliedFilters.date || undefined,
        search: appliedFilters.search || undefined,
        sortBy: appliedFilters.sortBy,
        sortOrder: appliedFilters.sortOrder,
        page: appliedFilters.page,
        limit: appliedFilters.limit,
      });

      if (response.success && response.data) {
        setAttendanceData(response.data);
        setPagination(response.pagination);

        const initialRemarks: Record<string, string> = {};
        const initialCheckInTimes: Record<string, string> = {};
        const initialCheckOutTimes: Record<string, string> = {};

        response.data.forEach((item) => {
          if (item.attendance) {
            initialRemarks[item.employeeId] = item.attendance.remarks || "";
            const latestIn = item.attendance.inOut
              ?.slice()
              .reverse()
              .find((r: InOutRecord) => r.type === "in");

            if (latestIn) {
              initialCheckInTimes[item.employeeId] = dayjs(
                latestIn.time
              ).format("HH:mm");
            }

            // Latest check-out punch
            const latestOutRecord = item.attendance.inOut
              ?.slice()
              .reverse()
              .find((rec: any) => rec.type === "out");

            if (latestOutRecord) {
              initialCheckOutTimes[item.employeeId] = dayjs(
                latestOutRecord.time
              ).format("HH:mm");
            }
          }
        });

        setRemarks(initialRemarks);
        setCheckInTimes(initialCheckInTimes);
        setCheckOutTimes(initialCheckOutTimes);
      }
    } catch (error) {
      console.error("Error fetching employee attendance:", error);
      message.error("Failed to fetch employee attendance data");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAttendance = async (employeeId: string) => {
    try {
      setMarkingLoading((prev) => ({ ...prev, [employeeId]: true }));

      const currentDate = dayjs(appliedFilters.date);

      // Prepare check-in time with proper ISO format
      let checkInTimeISO;
      if (checkInTimes[employeeId]) {
        const [hours, minutes] = checkInTimes[employeeId].split(":");
        checkInTimeISO = currentDate
          .hour(parseInt(hours))
          .minute(parseInt(minutes))
          .second(0)
          .millisecond(0)
          .toISOString();
      } else {
        checkInTimeISO = dayjs().toISOString(); // Use current time if no check-in time set
      }

      let checkOutTimeISO;
      if (checkOutTimes[employeeId]) {
        const [hours, minutes] = checkOutTimes[employeeId].split(":");
        checkOutTimeISO = currentDate
          .hour(parseInt(hours))
          .minute(parseInt(minutes))
          .second(0)
          .millisecond(0)
          .toISOString();
      } else {
        checkOutTimeISO = undefined;
      }

      // Send only check-in time and remarks, status will be calculated by backend
      const attendanceData = {
        attendances: [
          {
            employeeId: employeeId,
            remarks: remarks[employeeId] || "",
            date: appliedFilters.date,
            in: checkInTimeISO,
            out: checkOutTimeISO,
          },
        ],
      };

      console.log("Sending employee attendance data:", attendanceData);
      const response = await markEmployeeAttendance(attendanceData);

      if (response.success) {
        message.success("Attendance marked successfully");
        await fetchAttendanceData();
      } else {
        message.error(response.message || "Failed to mark attendance");
        await fetchAttendanceData();
      }
    } catch (error) {
      console.error("Error marking employee attendance:", error);
      message.error("Failed to mark attendance");
      await fetchAttendanceData();
    } finally {
      setMarkingLoading((prev) => ({ ...prev, [employeeId]: false }));
    }
  };

  const handleMarkAbsent = async (employeeId: string) => {
    try {
      setMarkingLoading((prev) => ({ ...prev, [employeeId]: true }));

      const response = await markEmployeeAttendance({
        attendances: [
          {
            employeeId,
            remarks: remarks[employeeId] || "",
            date: appliedFilters.date,
            status: "absent",
          },
        ],
      });

      if (response.success) {
        message.success("Marked absent");
        await fetchAttendanceData();
      } else {
        message.error(response.message || "Failed to mark attendance");
      }
    } catch (error) {
      console.error("Error marking absent:", error);
      message.error("Failed to mark attendance");
    } finally {
      setMarkingLoading((prev) => ({ ...prev, [employeeId]: false }));
    }
  };

  const handleMarkLeave = async (employeeId: string) => {
    try {
      setMarkingLoading((prev) => ({ ...prev, [employeeId]: true }));

      const response = await markEmployeeAttendance({
        attendances: [
          {
            employeeId,
            remarks: remarks[employeeId] || "",
            date: appliedFilters.date,
            status: "leave",
          },
        ],
      });

      if (response.success) {
        message.success("Marked on leave");
        await fetchAttendanceData();
      } else {
        message.error(response.message || "Failed to mark attendance");
      }
    } catch (error) {
      console.error("Error marking leave:", error);
      message.error("Failed to mark attendance");
    } finally {
      setMarkingLoading((prev) => ({ ...prev, [employeeId]: false }));
    }
  };

  const handleResetAttendance = async (employeeId: string) => {
    try {
      setMarkingLoading((prev) => ({ ...prev, [employeeId]: true }));

      const response = await resetEmployeeAttendance({
        date: appliedFilters.date,
        employeeIds: [employeeId],
      });

      if (response.success) {
        message.success(
          `Attendance reset! (${response.deletedCount} record deleted)`
        );
        await fetchAttendanceData();
      } else {
        message.error(response.message || "Failed to reset");
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || "Server error");
    } finally {
      setMarkingLoading((prev) => ({ ...prev, [employeeId]: false }));
      setSelectedEmployeeId(null);
    }
  };

  const handleRemarksChange = (employeeId: string, remark: string) => {
    setRemarks((prev) => ({
      ...prev,
      [employeeId]: remark,
    }));
  };

  const handleCheckInTimeChange = (employeeId: string, time: string | null) => {
    if (time) {
      setCheckInTimes((prev) => ({
        ...prev,
        [employeeId]: time,
      }));
    }
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      const newDate = date.format("YYYY-MM-DD");
      setFilters((prev) => ({
        ...prev,
        date: newDate,
      }));
      setAppliedFilters((prev) => ({
        ...prev,
        date: newDate,
        page: 1,
      }));
    }
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setAppliedFilters((prev) => ({
      ...prev,
      page,
      limit: pageSize,
    }));
  };

  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  const handleRefresh = async () => {
    try {
      await fetchAttendanceData();
      message.success("Data refreshed successfully");
    } catch (error) {
      message.error("Failed to refresh data");
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: "Employee Attendance Report",
  });

  const handleExportExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(
        attendanceData.map((item) => ({
          "Employee ID": item.employeeCode || "N/A",
          "Employee Name": `${item.user?.firstName || ""} ${
            item.user?.lastName || ""
          }`,
          Department: item.department || "N/A",
          Designation: item.designation || "N/A",
          Attendance: item.status || "Absent",
          "Check-In Time": item.attendance?.inOut?.find(
            (rec: InOutRecord) => rec.type === "in"
          )?.time
            ? dayjs(
                item.attendance.inOut.find(
                  (rec: InOutRecord) => rec.type === "in"
                )?.time
              ).format("HH:mm")
            : "N/A",
          "Check-Out Time": item.attendance?.inOut?.find(
            (rec: InOutRecord) => rec.type === "out"
          )
            ? dayjs(
                item.attendance.inOut.find(
                  (rec: InOutRecord) => rec.type === "out"
                )?.time
              ).format("HH:mm")
            : "N/A",
          Date: item.date ? new Date(item.date).toLocaleDateString() : "N/A",
          Remarks: item.attendance?.remarks || "",
        }))
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Attendance");
      XLSX.writeFile(workbook, "employee_attendance.xlsx");
      message.success("Exported to Excel successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      message.error("Failed to export to Excel");
    }
  };

  const handleSortSelect = (item: {
    label: string;
    value: {
      sortBy: string;
      sortOrder: string;
    };
  }) => {
    setSortBy(item);
    setAppliedFilters((prev) => ({
      ...prev,
      sortBy: item.value.sortBy,
      sortOrder: item.value.sortOrder as "asc" | "desc",
      page: 1,
    }));
  };

  useEffect(() => {
    fetchAttendanceData();
  }, [appliedFilters]);

  useEffect(() => {
    if (appliedFilters.search !== searchText) {
      const timeoutId = setTimeout(() => {
        setAppliedFilters((prev) => ({
          ...prev,
          search: searchText,
          page: 1,
        }));
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [searchText, appliedFilters.search]);

  const columns = [
    {
      title: "Employee ID",
      dataIndex: "employeeCode",
      render: (code: string) => code || "N/A",
      sorter: (a: any, b: any) => {
        const codeA = a.employeeCode || "";
        const codeB = b.employeeCode || "";
        return codeA.localeCompare(codeB);
      },
    },
    {
      title: "Name",
      dataIndex: "user",
      render: (user: any, record: any) => (
        <div className="d-flex align-items-center">
          <Link to="#" className="avatar avatar-md">
            <ImageWithBasePath
              src={"assets/img/profiles/avatar-01.jpg"}
              fullSrc={user?.profilePicture}
              className="img-fluid rounded-circle"
              alt="img"
            />
          </Link>
          <div className="ms-2">
            <p className="text-dark mb-0">
              <Link to="#">
                {`${user?.firstName || ""} ${user?.lastName || ""}`}
              </Link>
            </p>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => {
        const nameA = `${a.user?.firstName || ""} ${
          a.user?.lastName || ""
        }`.toLowerCase();
        const nameB = `${b.user?.firstName || ""} ${
          b.user?.lastName || ""
        }`.toLowerCase();
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Department",
      dataIndex: "department",
      render: (department: string) => department || "N/A",
      sorter: (a: any, b: any) => {
        const deptA = a.department || "";
        const deptB = b.department || "";
        return deptA.localeCompare(deptB);
      },
    },
    {
      title: "Designation",
      dataIndex: "designation",
      render: (designation: string) => designation || "N/A",
      sorter: (a: any, b: any) => {
        const desigA = a.designation || "";
        const desigB = b.designation || "";
        return desigA.localeCompare(desigB);
      },
    },
    {
      title: "Check-In Time",
      dataIndex: "attendance",
      render: (attendance: any, record: any) => {
        // Get initial value from existing attendance or user input
        const initialValue = checkInTimes[record.employeeId]
          ? dayjs(checkInTimes[record.employeeId], "HH:mm")
          : record.attendance?.inOut?.find(
              (rec: InOutRecord) => rec.type === "in"
            )?.time
          ? dayjs(
              record.attendance.inOut.find(
                (rec: InOutRecord) => rec.type === "in"
              )?.time
            )
          : null;

        return (
          <TimePicker
            format="HH:mm"
            value={initialValue}
            onChange={(time) =>
              handleCheckInTimeChange(
                record.employeeId,
                time ? time.format("HH:mm") : null
              )
            }
            disabled={markingLoading[record.employeeId]}
            style={{ width: "100px" }}
            placeholder="HH:mm"
          />
        );
      },
    },
    {
      title: "Check-Out Time",
      dataIndex: "attendance",
      render: (attendance: any, record: any) => {
        const stateOutTime = checkOutTimes[record.employeeId];

        const backendLatestOut = record.attendance?.inOut
          ?.slice()
          .reverse()
          .find((rec: any) => rec.type === "out");

        const displayOutTime =
          stateOutTime ||
          (backendLatestOut
            ? dayjs(backendLatestOut.time).format("HH:mm")
            : null);

        return (
          <TimePicker
            format="HH:mm"
            value={displayOutTime ? dayjs(displayOutTime, "HH:mm") : null}
            onChange={(time) =>
              time &&
              setCheckOutTimes((prev) => ({
                ...prev,
                [record.employeeId]: time.format("HH:mm"),
              }))
            }
            disabled={markingLoading[record.employeeId]}
            placeholder="HH:mm"
          />
        );
      },
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (_: any, record: any) => (
        <span className={`status-badge status-${record.status || "absent"}`}>
          {record.status
            ? record.status.charAt(0).toUpperCase() + record.status.slice(1)
            : "Absent"}
        </span>
      ),
      sorter: (a: any, b: any) => {
        const statusA = a.status || "";
        const statusB = b.status || "";
        return statusA.localeCompare(statusB);
      },
    },
    {
      title: "Action",
      dataIndex: "attendance",
      render: (attendance: any, record: any) => {
        const hasPermission =
          user.permissions?.includes("employee-attendance.create") ||
          !user.permissions?.length;
        // Without the permission there is nothing to click, so nothing is
        // drawn. Showing three dead buttons beside "(No permission)" only
        // invites people to press them — and the forced background colours
        // below made the disabled text unreadable on top of that.
        if (!hasPermission) {
          return <small className="text-muted">No permission to mark attendance</small>;
        }

        return (
          <div className="d-flex align-items-center flex-wrap gap-1">
            <Button
              type="primary"
              size="small"
              loading={markingLoading[record.employeeId]}
              onClick={() => handleMarkAttendance(record.employeeId)}
            >
              {markingLoading[record.employeeId]
                ? "Marking..."
                : "Mark Attendance"}
            </Button>
            <Button
              danger
              size="small"
              loading={markingLoading[record.employeeId]}
              onClick={() => handleMarkAbsent(record.employeeId)}
            >
              Mark Absent
            </Button>
            <Button
              size="small"
              style={{ backgroundColor: "#3B82F6", color: "#fff", border: "none" }}
              loading={markingLoading[record.employeeId]}
              onClick={() => handleMarkLeave(record.employeeId)}
            >
              Mark Leave
            </Button>
          </div>
        );
      },
    },
    {
      title: "Reset Attendance",
      dataIndex: "attendance",
      render: (attendance: any, record: any) => {
        const hasAttendanceMarked =
          record.attendance && record.attendance.inOut?.length > 0;

        const isDisabled =
          !hasAttendanceMarked ||
          (!user.permissions?.includes("employee-attendance.create") &&
            !!user.permissions?.length);

        return (
          <Button
            type="primary"
            size="small"
            loading={markingLoading[record.employeeId]}
            disabled={isDisabled}
            className={
              isDisabled
                ? "bg-secondary border-secondary text-white opacity-50 cursor-not-allowed"
                : "bg-primary"
            }
            data-bs-toggle="modal"
            data-bs-target="#delete-modal"
            onClick={() => setSelectedEmployeeId(record.employeeId)}
          >
            Reset Attendance
          </Button>
        );
      },
    },
    {
      title: "Notes",
      dataIndex: "remarks",
      render: (text: string, record: any) => (
        <div>
          <Input
            placeholder="Enter remarks"
            value={remarks[record.employeeId] || ""}
            onChange={(e) =>
              handleRemarksChange(record.employeeId, e.target.value)
            }
            disabled={markingLoading[record.employeeId]}
            style={{ minWidth: "150px" }}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Staff Attendance</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Report</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Staff Attendance
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={handleRefresh}
                onExportPdf={handlePrint}
                onExportExcel={handleExportExcel}
              />
            </div>
          </div>
          {/* /Page Header */}

          {/* Employee List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Staff Attendance List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="mb-3 me-2 position-relative">
                  <DatePicker
                    value={filters.date ? dayjs(filters.date) : null}
                    onChange={handleDateChange}
                    format="DD-MM-YYYY"
                    placeholder="Select Date"
                    disabledDate={(current) => {
                      return current && current > dayjs().endOf("day");
                    }}
                    style={{ height: "37px" }}
                  />
                </div>

                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {CLASSES_SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Staff Attendance</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              {/* Employee List */}
              <Table
                dataSource={attendanceData}
                columns={columns}
                loading={loading}
                setSearchText={handleSearch}
                pagination={{
                  ...pagination,
                  onChange: handlePaginationChange,
                }}
              />
              {/* /Employee List */}
            </div>
          </div>
          {/* /Employee List */}
        </div>
        <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form>
                <div className="modal-body text-center">
                  <span className="delete-icon">
                    <i className="ti ti-trash-x" />
                  </span>
                  <h4>Confirm Deletion</h4>
                  <p>
                    Are you sure you want to delete this driver? This action
                    cannot be undone.
                  </p>
                  <div className="d-flex justify-content-center">
                    <Link
                      to="#"
                      className="btn btn-light me-3"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      className="btn btn-danger"
                      data-bs-dismiss="modal"
                      onClick={() => {
                        if (selectedEmployeeId) {
                          handleResetAttendance(selectedEmployeeId);
                        }
                      }}
                    >
                      Yes, Reset
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendance;
